import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import type { AssignedLimitation } from "src/shared/api-types/limitations";
import ChipBox, { ChipBoxItem } from "src/components/ChipBox";

interface OperationLimitationsCellProps {
  enabled: boolean;
  limitations: AssignedLimitation[];
  canEdit: boolean;
  onClick: () => void;
}

const OperationLimitationsCell = ({ enabled, limitations, canEdit, onClick }: OperationLimitationsCellProps) => {
  const { t } = useTranslation();
  const clickable = canEdit && enabled;
  const limitationChips: ChipBoxItem[] = limitations.map((limitation) => ({
    id: limitation.definitionId,
    label: limitation.displayValue,
  }));

  const content = (() => {
    if (!enabled) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.disabled",
          }}
        >
          {t("limitations.not_applicable")}
        </Typography>
      );
    }
    if (limitations.length === 0) {
      return <Typography variant="body2">{t("limitations.no_limitations")}</Typography>;
    }

    return <ChipBox items={limitationChips} />;
  })();

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        cursor: clickable ? "pointer" : "default",
        borderRadius: 1,
        minHeight: 36,
        px: 1,
        py: 0.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "&:hover": clickable
          ? {
              bgcolor: "action.hover",
            }
          : undefined,
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{content}</Box>
      {clickable && <EditIcon fontSize="small" color="action" sx={{ opacity: 0.6, ml: 1 }} />}
    </Box>
  );
};

export default OperationLimitationsCell;
